import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { ArchivosManager } from "managers/ECF/archivos.manager";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { useTheme } from "hooks/useTheme";

// Tipos MIME aceptados para los archivos Excel (.xls / .xlsx). Tomado del
// patrón existente en components/DropZone/Dropzone.jsx (getAcceptedExtensions("excel")).
const EXCEL_ACCEPT = {
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

const PlantillaStep = ({
  tipo,
  onArchivoSeleccionado,
  showLoading,
  hideLoading,
  disabled,
}) => {
  const { handleException } = useExceptionHandler();
  const { Warning } = useMessage();
  const { theme } = useTheme();
  // Mantenemos el archivo seleccionado en estado local; sólo se procesa cuando
  // el usuario confirma con el botón "Procesar" (no se avanza automáticamente).
  // Se guarda como array porque ArchivosManager.cargarArchivo / postWithFiles
  // requieren `Files` como array (postWithFiles llama data.Files.map(...)).
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);

  const handleDescargarPlantilla = () => {
    showLoading && showLoading();
    const link = document.createElement("a");
    link.target = "_blank";
    ArchivosManager.descargarPlantilla(tipo)
      .then((blob) => {
        link.href = URL.createObjectURL(blob);
        link.click();
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading && hideLoading());
  };

  const handleDrop = (aceptados, rechazados) => {
    if (rechazados && rechazados.length > 0) {
      Warning({ message: "Solo se aceptan archivos Excel (.xls o .xlsx)" });
      return;
    }
    if (!aceptados || aceptados.length === 0) return;

    const archivo = aceptados[0];
    const ext = archivo.name.split(".").pop().toLowerCase();
    if (ext !== "xls" && ext !== "xlsx") {
      Warning({ message: "Solo se aceptan archivos Excel (.xls o .xlsx)" });
      return;
    }

    // No procesamos aún: guardamos la selección y esperamos el botón "Procesar".
    setArchivoSeleccionado(aceptados);
  };

  const handleQuitar = () => setArchivoSeleccionado(null);

  const handleProcesar = () => {
    if (archivoSeleccionado && archivoSeleccionado.length > 0) {
      onArchivoSeleccionado(archivoSeleccionado);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: EXCEL_ACCEPT,
    multiple: false,
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    disabled,
  });

  const stepCircle = (numero) => (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: theme.color1,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "0.8rem",
        flexShrink: 0,
        marginRight: 10,
      }}
    >
      {numero}
    </div>
  );

  return (
    <div>
      <Row className="g-3 align-items-stretch">
        <Col md={5}>
          <div
            className="h-100 p-4"
            style={{ border: "1px solid #dee2e6", borderRadius: 8 }}
          >
            <div className="d-flex align-items-center mb-3">
              {stepCircle(1)}
              <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                Descarga la plantilla
              </span>
            </div>
            <p
              className="text-muted mb-4"
              style={{ fontSize: "0.85rem", lineHeight: 1.6 }}
            >
              Descarga el archivo Excel con las columnas requeridas y complétalo
              con tus datos.
            </p>
            <div style={{ "--file-uploader-color": theme.color1 }}>
              <button
                type="button"
                className="StyledUploadButton"
                onClick={handleDescargarPlantilla}
                disabled={disabled}
                style={{ width: "100%" }}
              >
                <i className="fas fa-file-download" />
                <span>Descargar Plantilla</span>
              </button>
            </div>
          </div>
        </Col>

        <Col
          md={2}
          className="d-none d-md-flex align-items-center justify-content-center"
        >
          <i
            className="fas fa-arrow-right"
            style={{ fontSize: "1.3rem", color: "#adb5bd" }}
          />
        </Col>

        <Col md={5}>
          <div
            className="h-100 p-4"
            style={{ border: "1px solid #dee2e6", borderRadius: 8 }}
          >
            <div className="d-flex align-items-center mb-3">
              {stepCircle(2)}
              <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                Carga el archivo completado
              </span>
            </div>
            <p
              className="text-muted mb-3"
              style={{ fontSize: "0.85rem", lineHeight: 1.6 }}
            >
              Arrastra el archivo Excel o haz clic para
              seleccionarlo. Se aceptan los formatos <strong>.xls</strong> y{" "}
              <strong>.xlsx</strong>.
            </p>
            <div style={{ "--file-uploader-color": theme.color1 }}>
              {!archivoSeleccionado ? (
                <div
                  {...getRootProps({
                    className: "StyledUploadButton",
                    style: {
                      width: "100%",
                      cursor: disabled ? "not-allowed" : "pointer",
                      borderStyle: isDragActive ? "dashed" : "solid",
                      opacity: disabled ? 0.4 : 1,
                      filter: disabled ? "grayscale(100%)" : "none",
                    },
                  })}
                >
                  <input {...getInputProps()} />
                  <i className="fas fa-file-upload" />
                  <span>
                    {isDragActive ? "Suelta el archivo aquí" : "Cargar Plantilla"}
                  </span>
                </div>
              ) : (
                <div>
                  <div
                    className="d-flex align-items-center justify-content-between p-2 mb-3"
                    style={{
                      border: "1px solid #dee2e6",
                      borderRadius: 8,
                      background: "#f8f9fa",
                    }}
                  >
                    <span
                      className="d-flex align-items-center text-truncate"
                      style={{ fontSize: "0.85rem", minWidth: 0 }}
                    >
                      <i
                        className="fas fa-file-excel me-2"
                        style={{ color: "#198754", flexShrink: 0 }}
                      />
                      <span className="text-truncate">
                        {archivoSeleccionado[0].name}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-muted p-0 ms-2"
                      onClick={handleQuitar}
                      disabled={disabled}
                      style={{ flexShrink: 0, textDecoration: "none" }}
                      title="Quitar archivo"
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="StyledUploadButton"
                    onClick={handleProcesar}
                    disabled={disabled}
                    style={{ width: "100%" }}
                  >
                    <i className="fas fa-cogs" />
                    <span>Procesar</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default PlantillaStep;
